import { LegalLayout, type LegalSection } from '@mgp/ui';

const SECTIONS: LegalSection[] = [
  {
    id: 'data-we-collect',
    number: '1',
    title: 'Data we collect',
    body: (
      <p>
        We collect the personal data needed to operate the platform: email address, advertising
        activity (ads submitted, performance metrics, targeting choices), IP address, basic device
        information, and gameplay events for player accounts. For advertisers, we additionally
        collect wallet addresses and transaction hashes submitted with top-up payments.
      </p>
    ),
    subsections: [
      {
        id: 'data-we-collect-account',
        number: '1.1',
        title: 'Account data',
        body: (
          <p>
            Email address, account role (player, advertiser, admin), language preference, consent
            flags, and the time of your last sign-in.
          </p>
        ),
      },
      {
        id: 'data-we-collect-activity',
        number: '1.2',
        title: 'Activity data',
        body: (
          <p>
            Gameplay sessions, scores, leaderboard entries, ad impression and click events,
            geographic region inferred from IP address, and device profile information used to
            secure the account and prevent abuse.
          </p>
        ),
      },
      {
        id: 'data-we-collect-financial',
        number: '1.3',
        title: 'Financial data (advertisers only)',
        body: (
          <p>
            Connected wallet address, on-chain transaction hashes for top-ups, and the amount
            credited to your advertiser balance after admin approval.
          </p>
        ),
      },
    ],
  },
  {
    id: 'purpose',
    number: '2',
    title: 'Purpose',
    body: (
      <p>
        Your data is used for platform operation, analytics, ad delivery, fraud and anti-cheat
        detection, and payment verification. Advertiser data is additionally used for campaign
        optimization and billing reconciliation. We do not sell personal data.
      </p>
    ),
  },
  {
    id: 'retention',
    number: '3',
    title: 'Data retention',
    body: (
      <p>
        Personal data is retained indefinitely until you request deletion. Aggregated, anonymized
        data (e.g. spend totals, leaderboard standings) may be retained for analytics purposes
        even after your account is deleted.
      </p>
    ),
  },
  {
    id: 'deletion',
    number: '4',
    title: 'Deletion rights',
    body: (
      <p>
        You may request the deletion of your personal data at any time. Upon receiving a verified
        request we will permanently remove your personal data within 30 days. Audit-log entries
        required for financial and regulatory compliance may be retained in pseudonymized form
        for the minimum period required by law.
      </p>
    ),
  },
  {
    id: 'sharing',
    number: '5',
    title: 'Third-party sharing',
    body: (
      <p>
        We share only anonymized aggregates with third parties. Service providers include Resend
        (transactional email), Cloudflare (DNS, SSL, abuse prevention via Turnstile), and the
        platform&rsquo;s payment processors. These providers process data solely on our behalf
        under strict confidentiality agreements.
      </p>
    ),
  },
  {
    id: 'age',
    number: '6',
    title: 'Age restrictions',
    body: (
      <p>
        The platform does not implement an age gate. By using the platform, you represent that
        you are of legal age in your jurisdiction to enter into binding agreements.
      </p>
    ),
  },
  {
    id: 'contact',
    number: '7',
    title: 'Contact',
    body: (
      <p>
        For privacy inquiries or data deletion requests, contact us at{' '}
        <a
          href="mailto:privacy@adspert.io"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          privacy@adspert.io
        </a>
        .
      </p>
    ),
  },
];

export function PrivacyPage() {
  return (
    <LegalLayout
      eyebrow="Privacy"
      title="Privacy Policy"
      lastUpdated="2026-04-19"
      intro={
        <p>
          We treat data carefully and explain what we collect, why, and how long we keep it. If
          anything below is unclear, write to us — we read every privacy email.
        </p>
      }
      sections={SECTIONS}
    />
  );
}
